import _ from "underscore";

import { 
	SYNC_RELEVANCY_LEFT, 
	SYNC_RELEVANCY_RIGHT, 
	SET_CURRENT_MAP_ID,
	SET_FILTER_BY_MAP_COUNTRY,
} from "actions/constants.js";

import {
	fetchMapDataCompare,
	fetchTimeseriesCompare,
	fetchCompareArticles,
	fetchComparePhrases,
	fetchCompareEntities,
} from "actions/fetch";

import { 
	addOrRemove,
	mergeEntityFilters,
} from "@exo/helpers";

import { 
	activateProposedFilters, 
	reactivateFilters,
	genCountryFilter,
	hasCountryFilters,
	removeSourceCountryFilters,
	removeStateFilters,
	getCountryFilters,
} from "lib/helpers/filterHelpers.js";

import { 
	alignGraphDataTypeByPerspective, 
	makeFetchKey 
} from "lib/helpers/helpers.js";

const CONTENT_PAGE_LENGTH = 50;

// one action to update all data for the Narratives tab
//		Updates Map, Graph, Article, and Entities data
// 		Not for MOEs
export function updateDataCompare(parameters) {
	const {
		side,
		activeFilters,
		contentFilteredByDate,
		currentZoom,
		entityFilters,
		eventMode,
		keywords,
		mapHighlightId,
		narrative,
		perspective,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("UD Compare =>", narrative);
	const narrativeCopy = JSON.parse(JSON.stringify(narrative));

	return (dispatch, getState) => {
		// The base filters are clean
		const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;
		const {
			articlesPageOffset,
			contentHeaderSortTypeSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortAscendingAudience,
			dmaAggregation,
			graphDataTypeActive,
			showDuplicates,
			relevancy,
			filterByMapCountry,
			currentMapCountryFilter,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;
		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, activeFilters || []));
		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		const { compareMultiSelect, graphDataTypes, hideMap, showTopicsInGraph } = getState().projectConfig;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const numberOfLinesWithParent = narrative.children && narrative.children.length > 0 ? narrative.children.length + 1 : 1;
		const numberOfLinesWithoutParent = narrative.children && narrative.children.length > 0 ? narrative.children.length : 1;
		// Show parent timeseries if showTopicsInGraph or there are no children
		// Also, dont' fetch the base object
		const showTopicLine = (showTopicsInGraph && narrative.parent !== "self") || !narrative.children || narrative.children.length < 1;

		const alignedDataType = alignGraphDataTypeByPerspective({ perspective, graphDataTypeActive, graphDataTypes });

		const fetchKey = makeFetchKey(narrativeCopy, perspective, activeFilters, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		const getChildren = !compareMultiSelect || !narrativeCopy.compositeNarratives || narrativeCopy.compositeNarratives.length <= 1;

		// prepare state
		dispatch({
			type: eventMode ? `updateEventVariables${side}` : `updateVariables${side}`, // ONLY updates varStore
			narrative: narrativeCopy,
			perspective,
			activeFilters,
			entityFilters,
			currentZoom,
			mapHighlightId,
			contentFilteredByDate,
		});

		// Check if map country filtering should be disabled based on country filter configuration
		if (filterByMapCountry) {
			const activeCountryFilters = getCountryFilters(activeFilters);
			let foundInvalidCountry = false;
			for (let i=0; i<activeCountryFilters.length; i++) {
				if (currentMapCountryFilter !== activeCountryFilters[i].id) {
					foundInvalidCountry = true;
					break;
				}
			}
			if (foundInvalidCountry || activeCountryFilters.length === 0) {
				dispatch({
					type: `${SET_FILTER_BY_MAP_COUNTRY}_${side}`,
					value: false
				});
			}
		}

		// Request time series data
		dispatch({
			type: `requestTimeseries${side}`,
			numberOfLines: getChildren
				? showTopicsInGraph && narrativeCopy.parent !== "self"
					? numberOfLinesWithParent
					: numberOfLinesWithoutParent
				: 1,
			narrative: narrativeCopy,
		});

		// Request Articles
		dispatch({ type: `requestArticles${side}` });

		const mergedFilters = mergeEntityFilters({
			activeFilters,
			entityFilters,
			keywords,
			perspective,
		});

		const startDate = contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart;
		const endDate = contentFilteredByDate ? Number(contentFilteredByDate.end) : currentZoom.end || backUpNarrative.rangeEnd;
		const params = {
			narrative: narrativeCopy,
			perspective,
			filtersList: mergedFilters || [],
			currentFilters,
			startDate,
			endDate,
			side,
			notMOE: true,
			stateCode: mapHighlightId, // for the state that you want county data for, or false.
			dmaAggregation,
			dataType: alignedDataType,
			fetchKey,
			eventMode,
			relevancy,
			showDuplicates,
		};

		if (!hideMap) {
			dispatch(fetchMapDataCompare(params));
		}

		if (showTopicLine) {
			dispatch(fetchTimeseriesCompare(params));
		}

		// don't allow children if multiple narratives are selected
		if (getChildren) {
			_.each(narrativeCopy.children, (trend) => {
				dispatch(
					fetchTimeseriesCompare({
						narrative: trend,
						currentFilters,
						dataType: alignedDataType,
						endDate,
						eventMode,
						fetchKey,
						filtersList: mergedFilters || [],
						notMOE: true,
						parent: narrativeCopy,
						perspective,
						relevancy,
						side,
						startDate,
						stateCode: mapHighlightId, // for the state that you want county data for, or false.
					})
				);
			});
		}

		const articleParams = params;
		articleParams.sortBy = contentHeaderSortType;
		articleParams.ascending = contentHeaderSortAscending;
		articleParams.pageLength = CONTENT_PAGE_LENGTH;
		articleParams.offset = articlesPageOffset;

		dispatch(fetchCompareArticles(articleParams));
		// dispatch(fetchComparePhrases(params));
		// No entities in Reaction mode
		if (perspective !== "Audience") {
			// FLAG_REMOVE_COMPARE_ENTITIES
			// REVISIT JA: Due to API performance issues, entity view is currently removed from Compare 
			// dispatch({ type: `requestEntities${side}` });
			// dispatch(fetchCompareEntities(params));
		}
		dispatch({
			type: side === "Left" ? SYNC_RELEVANCY_LEFT : SYNC_RELEVANCY_RIGHT,
			payload: { relevancy, activeRelevancy: relevancy },
		});
	};
}

export function updateMapDataCompare(parameters) {
	const {
		side,
		activeFilters,
		contentFilteredByDate,
		currentZoom,
		entityFilters,
		eventMode,
		keywords,
		mapHighlightId,
		narrative,
		perspective,
		updatedMapSegment,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("UD Compare =>", narrative);
	const narrativeCopy = JSON.parse(JSON.stringify(narrative));

	return (dispatch, getState) => {
		// The base filters are clean
		const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;
		const { dmaAggregation, showDuplicates, relevancy } = side === "Left" ? getState().leftCompare : getState().rightCompare;
		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, activeFilters || []));
		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		const fetchKey = makeFetchKey(narrativeCopy, perspective, activeFilters, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		const mergedFilters = mergeEntityFilters({
			activeFilters,
			entityFilters,
			keywords,
			perspective,
		});

		const startDate = contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart;
		const endDate = contentFilteredByDate
			? contentFilteredByDate.begin // fetch functions add a day automatically
			: currentZoom.end || backUpNarrative.rangeEnd;

		dispatch(
			fetchMapDataCompare({
				narrative: narrativeCopy,
				perspective,
				filtersList: mergedFilters || [],
				currentFilters,
				dmaAggregation,
				startDate,
				endDate,
				side,
				notMOE: true,
				stateCode: mapHighlightId, // for the state that you want county data for, or false.
				fetchKey,
				eventMode,
				relevancy,
				showDuplicates,
				currentMapSegmentSpan: updatedMapSegment,
			})
		);
	};
}

export function updateCurrentMapDataCompare({data, side}) {
	return (dispatch, getState) => {
		const { location } = getState().router;
		const {
			activeKeywords,
			activeNarrativeData,
			contentFilteredByDate,
			filtersList,
			currentZoom,
			entityFilters,
			mapHighlightId,
			perspective,
			currentMapSegmentSpan,
		} = data;
		const eventMode = location && location.pathname === "/events";
		return () => dispatch(
			updateMapDataCompare({
				side,
				activeFilters: filtersList,
				contentFilteredByDate,
				currentZoom,
				entityFilters,
				eventMode,
				keywords: activeKeywords,
				mapHighlightId,
				narrative: activeNarrativeData,
				perspective,
				updatedMapSegment: currentMapSegmentSpan,
			})
		);
	}
}

export function updateGraphDataCompare(parameters) {
	const { dataType, side } = parameters;

	return (dispatch, getState) => {
		const {
			activeKeywords,
			activeNarrativeData,
			contentFilteredByDate,
			currentZoom,
			entityFilters,
			filtersList,
			perspective,
			relevancy,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare; // prettier-ignore
		// The base filters are clean
		const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;
		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, filtersList));
		const narrative = JSON.parse(JSON.stringify(activeNarrativeData));

		const { showTopicsInGraph, compareMultiSelect } = getState().projectConfig;

		const numberOfLinesWithParent = narrative.children && narrative.children.length > 0 ? narrative.children.length + 1 : 1;
		const numberOfLinesWithoutParent = narrative.children && narrative.children.length > 0 ? narrative.children.length : 1;
		// Show parent timeseries if showTopicsInGraph or there are no children
		// Also, dont' fetch the base object
		const showTopicLine = (showTopicsInGraph && narrative.parent !== "self") || !narrative.children || narrative.children.length < 1;

		const fetchKey = makeFetchKey(narrative, perspective, filtersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		const getChildren = !compareMultiSelect || !narrative.compositeNarratives || narrative.compositeNarratives.length <= 1;

		dispatch({
			type: `requestTimeseries${side}`,
			numberOfLines: getChildren
				? showTopicsInGraph && narrative.parent !== "self"
					? numberOfLinesWithParent
					: numberOfLinesWithoutParent
				: 1,
			narrative,
			noSpinner: true, // disable whole side spinner
		});

		const mergedFilters = mergeEntityFilters({
			activeFilters: filtersList,
			entityFilters,
			keywords: activeKeywords,
			perspective,
		});

		const startDate = contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin;
		const endDate = contentFilteredByDate
			? contentFilteredByDate.begin // fetch functions add a day automatically
			: currentZoom.end;

		if (showTopicLine) {
			dispatch(
				fetchTimeseriesCompare({
					narrative,
					currentFilters,
					dataType,
					endDate,
					eventMode: false,
					fetchKey,
					filtersList: mergedFilters || [],
					startDate,
					notMOE: true,
					perspective,
					relevancy,
					side,
				})
			);
		}

		// don't allow children if multiple narratives are selected
		if (getChildren) {
			_.each(narrative.children, (trend) => {
				dispatch(
					fetchTimeseriesCompare({
						narrative: trend,
						currentFilters,
						dataType,
						endDate,
						eventMode: false,
						fetchKey,
						filtersList: mergedFilters || [],
						notMOE: true,
						parent: narrative,
						perspective,
						relevancy,
						side,
						startDate,
						// stateCode: mapHighlightId, // for the state that you want county data for, or false.
					})
				);
			});
		}
	};
}

// one action to update all data for the Narratives tab
//		Updates Map and Graph only
export function updateDataMoe(parameters) {
	const { side, activeFilters, currentZoom, entityFilters, keywords, mapHighlightId, narrative, perspective } = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log(
	// 	"UD MOE =>",
	// 	side,
	// 	narrative,
	// 	perspective,
	// 	currentZoom,
	// 	activeFilters,
	// 	mapHighlightId
	// );

	return (dispatch, getState) => {
		const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;
		const { graphDataTypes, hideMap, moeGraphAvailable, moeMapAvailable } = getState().projectConfig;
		const { noMoe } = getState().base;
		const { dmaAggregation, graphDataTypeActive, relevancy } = side === "Left" ? getState().leftCompare : getState().rightCompare;
		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, activeFilters || []));
		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		const alignedDataType = alignGraphDataTypeByPerspective({ perspective, graphDataTypeActive, graphDataTypes });

		const fetchKey = makeFetchKey(narrative, perspective, activeFilters, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: `updateMoeVariables${side}`, // ONLY updates varStore
			narrative,
			perspective,
			activeFilters,
			currentZoom,
			entityFilters,
			mapHighlightId,
		});

		const mergedFilters = mergeEntityFilters({
			activeFilters,
			entityFilters,
			keywords,
			perspective,
		});

		if (!hideMap && moeMapAvailable) {
			dispatch(
				fetchMapDataCompare({
					narrative,
					perspective,
					filtersList: mergedFilters || [],
					currentFilters,
					startDate: currentZoom.begin || backUpNarrative.rangeStart,
					endDate: currentZoom.end || backUpNarrative.rangeEnd,
					side,
					notMOE: noMoe || (narrative.parent !== "moe" && narrative.parent !== "MOE"),
					relevancy,
					stateCode: mapHighlightId, // for the state that you want county data for, or false.
					dmaAggregation,
					fetchKey,
				})
			);
		}

		dispatch({
			type: `requestTimeseries${side}`,
			numberOfLines: narrative.children ? narrative.children.length + 1 : 1,
			narrative,
			noSpinner: !moeGraphAvailable,
		});
		if (moeGraphAvailable) {
			dispatch(
				fetchTimeseriesCompare({
					narrative,
					perspective,
					dataType: alignedDataType,
					filtersList: mergedFilters || [],
					currentFilters,
					startDate: currentZoom.begin || backUpNarrative.rangeStart,
					endDate: currentZoom.end || backUpNarrative.rangeEnd,
					side,
					notMOE: noMoe || (narrative.parent !== "moe" && narrative.parent !== "MOE"),
					relevancy,
					stateCode: mapHighlightId, // for the state that you want county data for, or false.
					fetchKey,
					eventMode: false,
				})
			);
			_.each(narrative.children, (trend) => {
				dispatch(
					fetchTimeseriesCompare({
						narrative: trend,
						perspective,
						dataType: alignedDataType,
						filtersList: mergedFilters || [],
						currentFilters,
						startDate: currentZoom.begin || backUpNarrative.rangeStart,
						endDate: currentZoom.end || backUpNarrative.rangeEnd,
						relevancy,
						side,
						notMOE: noMoe || (narrative.parent !== "moe" && narrative.parent !== "MOE"),
						stateCode: mapHighlightId, // for the state that you want county data for, or false.
						fetchKey,
						eventMode: false,
					})
				);
			});
		}
	};
}

// one action to update Articles and Entities for the Narratives tab
//		For use with zooming the graph
// 		Works with 'all'
export function updateLowerDataCompare(parameters) {
	const {
		side,
		activeFilters,
		contentFilteredByDate,
		currentZoom,
		entityFilters,
		keywords,
		mapHighlightId,
		narrative,
		narType, // leftMOE or left || rightMOE or right
		perspective,
		offset,
		pageLength,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("Update Lower Data =>", activeFilters, contentFilteredByDate);

	return (dispatch, getState) => {
		const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;
		const {
			articlesPageOffset,
			contentHeaderSortAscendingAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortTypeSource,
			showDuplicates,
			relevancy,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;
		const { location } = getState().router;
		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, activeFilters || []));

		const fetchKey = makeFetchKey(narrative, perspective, activeFilters, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: location && location.pathname === "/events" ? "updateLowerEventVariablesCompare" : "updateLowerVariablesCompare", // ONLY updates varStore
			contentFilteredByDate,
			currentZoom,
			perspective,
			narrative,
			narType,
		});
		dispatch({ type: `requestArticles${side}` });

		const mergedFilters = mergeEntityFilters({
			activeFilters,
			entityFilters,
			keywords,
			perspective,
		});

		const params = {
			currentFilters,
			endDate: currentZoom.end || backUpNarrative.rangeEnd,
			fetchKey,
			filtersList: mergedFilters || [],
			narrative,
			notMOE: true,
			perspective,
			relevancy,
			showDuplicates,
			side,
			stateCode: mapHighlightId,
			startDate: currentZoom.begin || backUpNarrative.rangeStart,
		};
		const articleParams = params;
		articleParams.sortBy = contentHeaderSortType;
		articleParams.ascending = contentHeaderSortAscending;
		articleParams.pageLength = pageLength !== undefined ? pageLength : CONTENT_PAGE_LENGTH;
		articleParams.offset = offset !== undefined ? offset : articlesPageOffset;
		dispatch(fetchCompareArticles(articleParams));
		// dispatch(fetchComparePhrases(params));
		// No entities in Reaction mode
		if (perspective !== "Audience") {
			// FLAG_REMOVE_COMPARE_ENTITIES
			// REVISIT JA: Due to API performance issues, entity view is currently removed from Compare 
			// dispatch({ type: `requestEntities${side}` });
			// dispatch(fetchCompareEntities(params));
		}
	};
}

// one action to update Articles and Entities for the Narratives tab
//		For use with updateContentByDate. Stores the indicator (or clears it)
// 		Works with 'all'
export function updateDataByDateCompare(parameters) {
	const {
		activeFilters,
		contentFilteredByDate,
		currentZoom,
		entityFilters,
		keywords,
		mapHighlightId,
		narrative,
		narType,
		perspective,
		side,
		offset,
		pageLength,
	} = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null
	// console.log("Update Data by date =>", currentZoom, activeFilters, contentFilteredByDate);

	return (dispatch, getState) => {
		const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;
		const {
			articlesPageOffset,
			contentHeaderSortAscendingAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortTypeSource,
			showDuplicates,
			relevancy,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;
		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, activeFilters || []));

		const fetchKey = makeFetchKey(narrative, perspective, activeFilters, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: "updateVariablesByDateCompare", // ONLY updates varStore
			narrative,
			perspective,
			contentFilteredByDate,
			narType,
		});
		dispatch({ type: `requestArticles${side}` });

		const mergedFilters = mergeEntityFilters({
			activeFilters,
			entityFilters,
			keywords,
			perspective,
		});

		const params = {
			currentFilters,
			endDate: contentFilteredByDate ? Number(contentFilteredByDate.end) : currentZoom.end || backUpNarrative.rangeEnd,
			fetchKey,
			filtersList: mergedFilters || [],
			narrative,
			notMOE: true,
			perspective,
			relevancy,
			showDuplicates,
			side,
			stateCode: mapHighlightId, // for the state that you want county data for, or false.
			startDate: contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart,
		};

		const articleParams = params;
		articleParams.sortBy = contentHeaderSortType;
		articleParams.ascending = contentHeaderSortAscending;
		articleParams.pageLength = pageLength !== undefined ? pageLength : CONTENT_PAGE_LENGTH;
		articleParams.offset = offset !== undefined ? offset : articlesPageOffset;

		dispatch(fetchCompareArticles(articleParams));
		// dispatch(fetchComparePhrases(params));
		// No entities in Reaction mode
		if (perspective !== "Audience") {
			// FLAG_REMOVE_COMPARE_ENTITIES
			// REVISIT JA: Due to API performance issues, entity view is currently removed from Compare 
			// dispatch({ type: `requestEntities${side}` });
			// dispatch(fetchCompareEntities(params));
		}
	};
}

// Updates artices only. Used with the show duplicates toggle
//   NOTE: this doesn't update the varStore, since no tracked variables are changing
export function updateArticleDataCompare(parameters) {
	const { showDuplicates, side } = parameters;
	// narrative = object. Must have id & parent
	// activeFilters = array of any active filters or null

	return (dispatch, getState) => {
		const narrative = side === "Left" ? getState().leftCompare.activeNarrativeData : getState().rightCompare.activeNarrativeData;
		const {
			activeKeywords,
			contentFilteredByDate,
			contentHeaderSortTypeSource,
			contentHeaderSortTypeAudience,
			contentHeaderSortAscendingSource,
			contentHeaderSortAscendingAudience,
			currentZoom,
			entityFilters,
			mapHighlightId,
			perspective,
			relevancy,
			sourceFilters,
			audienceFilters,
		} = side === "Left" ? getState().leftCompare : getState().rightCompare;

		const { varStore } = getState().storage;

		const sideVarStore = side === "Left" ? varStore.left : varStore.right;
		const activeFilters = sideVarStore[perspective].filters;		
		const baseFilters = perspective === "Source" ? sourceFilters : audienceFilters;
		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, activeFilters || []));

		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		// Set the sort variables by perspective
		const contentHeaderSortType = perspective === "Source" ? contentHeaderSortTypeSource : contentHeaderSortTypeAudience;
		const contentHeaderSortAscending = perspective === "Source" ? contentHeaderSortAscendingSource : contentHeaderSortAscendingAudience;

		const fetchKey = makeFetchKey(narrative, perspective, activeFilters, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({ type: `requestArticles${side}` });

		const mergedFilters = mergeEntityFilters({
			activeFilters,
			entityFilters,
			keywords: activeKeywords,
			perspective,
		});

		dispatch(
			fetchCompareArticles({
				ascending: contentHeaderSortAscending,
				currentFilters,
				endDate: contentFilteredByDate
					? contentFilteredByDate.begin // fetch functions add a day automatically
					: currentZoom.end || backUpNarrative.rangeEnd,
				fetchKey,
				filtersList: mergedFilters || [],
				narrative,
				notMOE: true,
				perspective,
				relevancy,
				showDuplicates,
				side,
				sortBy: contentHeaderSortType,
				startDate: contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart,
				stateCode: mapHighlightId, // for the state that you want county data for, or false.
			})
		);
	};
}

// one action to update Entities
//		For use with the translate endities toggle
// 		Works with 'all'
export function updateEntityDataCompare({ translateEntities, side }) {
	return (dispatch, getState) => {
		const { activeKeywords, activeNarrativeData, contentFilteredByDate, currentZoom, entityFilters, filtersList, perspective, relevancy } =
			side === "Left" ? getState().leftCompare : getState().rightCompare;
		const baseFilters = perspective === "Source" ? getState().base.sourceFilters : getState().base.audienceFilters;
		let backUpNarrative = getState().base.activeNarrativeData; // Make sure there is SOME data
		// Top level narrative may not have a time series. If so, grab first child
		backUpNarrative =
			!backUpNarrative.rangeStart && backUpNarrative.children && backUpNarrative.children[0]
				? backUpNarrative.children[0]
				: backUpNarrative;

		const currentFilters = activateProposedFilters(reactivateFilters(baseFilters, filtersList || []));
		const startDate = contentFilteredByDate ? Number(contentFilteredByDate.begin) : currentZoom.begin || backUpNarrative.rangeStart;
		const endDate = contentFilteredByDate ? Number(contentFilteredByDate.end) : currentZoom.end || backUpNarrative.rangeEnd;
		
		const fetchKey = makeFetchKey(activeNarrativeData, perspective, filtersList, currentZoom, relevancy);
		dispatch({ type: `mostRecentFetch${side}`, fetchKey });

		// prepare state
		dispatch({
			type: "updateLowerVariablesCompare", // ONLY updates varStore
			contentFilteredByDate,
			currentZoom,
			perspective,
			narrative: activeNarrativeData,
			narType: side.toLowerCase(), // assumes no moeMode, since these features don't currently overlap
		});

		const mergedFilters = mergeEntityFilters({
			activeFilters: filtersList,
			entityFilters,
			keywords: activeKeywords,
			perspective,
		});

		const params = {
			currentFilters,
			endDate,
			fetchKey,
			filtersList: mergedFilters || [],
			narrative: activeNarrativeData,
			perspective,
			relevancy,
			side,
			startDate,
			translateEntities,
		};

		// dispatch(fetchComparePhrases(params));
		// No entities in Reaction mode
		if (perspective !== "Audience") {
			dispatch({ type: `requestEntities${side}` });
			dispatch(fetchCompareEntities(params));
		}
	};
}

export function changeMap(id, stateIdPrefix, side) {
	return (dispatch, getState) => {
		const { varStore } = getState().storage;
		const { topicsPie } = getState().projectConfig;
		const { 
			activeKeywords,
			activeNarrativeData,
			currentMapId,
			currentFilters,
			filterByMapCountry, 
			perspective, 
		} = (side === "Left" ? getState().leftCompare : getState().rightCompare);
		const varStoreIndex = side.toLowerCase();

		dispatch({
			type: `${SET_CURRENT_MAP_ID}_${side}`,
			id,
			stateIdPrefix,
		});

		if (filterByMapCountry) {
			const filterObject = genCountryFilter({ 
				countryId: id, 
				active: filterByMapCountry, 
			});
			
			let newFilters = Array.isArray(currentFilters) ? currentFilters : [];
			newFilters = removeSourceCountryFilters(newFilters);
			newFilters = addOrRemove(newFilters, filterObject, "id");
			newFilters = removeStateFilters(newFilters, stateIdPrefix);

			dispatch(
				updateDataCompare({
					side,
					narrative: activeNarrativeData,
					perspective,
					currentZoom: varStore[varStoreIndex][perspective].currentZoom,
					entityFilters: varStore[varStoreIndex][perspective].entityFilters,
					activeFilters: newFilters,
					currentFilters,
					keywords: activeKeywords,
					mapHighlightId: null,
					contentFilteredByDate: varStore[varStoreIndex][perspective].contentFilteredByDate,
					topicsPie,
				})
			);
		}
	};
}

export const toggleFilterByMapCountry = (side) => {
	return (dispatch, getState) => {

		const { varStore } = getState().storage;
		const { topicsPie } = getState().projectConfig;
		const { 
			activeKeywords,
			activeNarrativeData,
			currentMapId,
			filtersList,
			filterByMapCountry, 
			perspective, 
			currentMapStateIdPrefix,
		} =	(side === "Left" ? getState().leftCompare : getState().rightCompare);
		const varStoreIndex = side.toLowerCase();

		const newState = !filterByMapCountry;
		const filterObject = genCountryFilter({ 
			countryId: currentMapId, 
			active: newState, 
		});

		let newFilters = Array.isArray(filtersList) ? filtersList : [];

		// If any country filters are set and changing to not filter by
		// displayed map country then clear all active country filters
		if (hasCountryFilters(newFilters) && !newState) {
			newFilters = removeSourceCountryFilters(newFilters);
		}

		// If any country filters are set and changing to filter by
		// displayed map country then remove all current country filters 
		// and replace with a single new country filter for the active map.
		// Also remove any state/province level filters set which are not
		// relevant to the current map country.
		else if (hasCountryFilters(newFilters)) {
			newFilters = removeSourceCountryFilters(newFilters);
			newFilters = addOrRemove(newFilters, filterObject, "id");
			newFilters = removeStateFilters(newFilters, currentMapStateIdPrefix);
		}

		// If no country filters are set and changing to filter by displayed
		// country then add a single new country filter for the active map
		// Also remove any state/province level filters set which are not
		// relevant to the current map country.
		else if (!hasCountryFilters(newFilters) && newState) {
			newFilters = addOrRemove(newFilters, filterObject, "id");
			newFilters = removeStateFilters(newFilters, currentMapStateIdPrefix);
		}

		// Should never be here (no filters set + moving out of a filtered
		// map state), just clear any active country filters
		else {
			newFilters = removeSourceCountryFilters(newFilters);
		}

		// Store changes & send API updates
		dispatch({
			type: `${SET_FILTER_BY_MAP_COUNTRY}_${side}`,
			value: newState,
		});

		dispatch(
			updateDataCompare({
				side,
				narrative: activeNarrativeData,
				perspective,
				currentZoom: varStore[varStoreIndex][perspective].currentZoom,
				entityFilters: varStore[varStoreIndex][perspective].entityFilters,
				activeFilters: newFilters,
				keywords: activeKeywords,
				mapHighlightId: null,
				contentFilteredByDate: varStore[varStoreIndex][perspective].contentFilteredByDate,
				topicsPie,
			})
		);
	};
};